/**
 * ContentItem — single source of truth for rendering HH deal items and Daily Special items.
 *
 * Used by:
 *   - HomePage.js (public site)
 *   - VenueEditor.js (live preview + content preview modal)
 *
 * Same JSX, same CSS everywhere. Change once, updates everywhere.
 */

import React from 'react';
import './ContentItem.css';

// ── Price badge normalization ─────────────────────────────────────────────────

export function normalizePriceBadge(price) {
  if (!price) return null;
  const p = String(price).trim();
  if (!p) return null;

  // Already a dollar amount
  if (/^\$\d/.test(p)) return p;

  // Bare number → dollar
  if (/^\d+(\.\d{1,2})?$/.test(p)) return `$${p}`;

  // Percent off  (e.g. "50%", "50% off", "50% OFF")
  const pctMatch = p.match(/^(\d+)\s*%/i);
  if (pctMatch) return `${pctMatch[1]}% OFF`;

  // Half off variations
  if (/half[\s-]*off|1\/2[\s-]*off|50[\s-]*off/i.test(p)) return '½ OFF';

  // BOGO
  if (/\bbogo\b|buy\s+one\s+get\s+one/i.test(p)) return 'BOGO';

  // Pass through anything else (e.g. "Free", "Market Price")
  return p;
}

// ── Redundancy suppression ────────────────────────────────────────────────────
// Returns true if description is just restating the name (+ price), so we
// can suppress it and avoid the double-display problem.

export function isRedundantDescription(name, description) {
  if (!description || !description.trim()) return true;

  // Strip price patterns from description
  const stripped = description
    .replace(/\$[\d.,]+/g, '')
    .replace(/\d+\s*%\s*(off)?/gi, '')
    .replace(/half[\s-]*off/gi, '')
    .replace(/bogo/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  if (!stripped) return true;

  // Normalize both strings: lowercase, strip punctuation, collapse whitespace
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
  const normName = norm(name || '');
  const normDesc = norm(stripped);

  if (!normName) return false;

  const nameWords = normName.split(' ').filter(w => w.length > 2);
  const descWords = new Set(normDesc.split(' ').filter(w => w.length > 2));

  if (nameWords.length === 0) return false;

  // Exact match always redundant; for the overlap check require ≥2 name words
  // so single-word items ("Pizza", "Wings") don't suppress descriptions that
  // merely mention the item in a caveat ("No coupons, excludes pizza of the month").
  if (normDesc === normName) return true;
  if (nameWords.length < 2) return false;
  // Don't suppress descriptions that add substantial new content — if the
  // description has significantly more words than the name, it's informative
  // even if the name words appear in it (e.g. "Margarita Flight" → desc starts with name).
  const descWordCount = normDesc.split(' ').filter(w => w.length > 2).length;
  if (descWordCount > nameWords.length * 2 + 2) return false;
  const overlap = nameWords.filter(w => descWords.has(w)).length;
  return (overlap / nameWords.length) >= 0.75;
}

// ── renderMarkdown ────────────────────────────────────────────────────────────
// Converts a small subset of markdown to React elements — no library needed.
// Supports: **bold**, *italic*, and lines starting with "- " as bullet lists.
// Plain text (no markdown syntax) renders identically to before — no regression.

export function renderMarkdown(text) {
  if (!text) return null;

  const renderInline = (str, keyBase) => {
    const parts = [];
    // Match **bold** or *italic* tokens
    const re = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g;
    let last = 0, i = 0;
    let m;
    while ((m = re.exec(str)) !== null) {
      if (m.index > last) parts.push(str.slice(last, m.index));
      const token = m[0];
      if (token.startsWith('**')) {
        parts.push(<strong key={`${keyBase}-${i++}`}>{token.slice(2, -2)}</strong>);
      } else {
        parts.push(<em key={`${keyBase}-${i++}`}>{token.slice(1, -1)}</em>);
      }
      last = re.lastIndex;
    }
    if (last < str.length) parts.push(str.slice(last));
    return parts;
  };

  const lines = text.split('\n');
  const output = [];
  let listItems = [];
  // Accumulate consecutive non-bullet text lines to render as one inline span
  let pendingInline = [];

  const flushList = () => {
    if (listItems.length) {
      output.push(<ul key={`ul-${output.length}`} className="ci-md-list">{listItems}</ul>);
      listItems = [];
    }
  };

  const flushInline = () => {
    if (!pendingInline.length) return;
    // Join non-empty lines with a space so multiple lines flow as one sentence
    const parts = [];
    pendingInline.forEach((chunks, i) => {
      if (i > 0) parts.push(' ');
      parts.push(...chunks);
    });
    output.push(<span key={`inline-${output.length}`} className="ci-md-line">{parts}</span>);
    pendingInline = [];
  };

  lines.forEach((line, idx) => {
    if (/^- /.test(line)) {
      flushInline();
      listItems.push(<li key={idx}>{renderInline(line.slice(2), `li-${idx}`)}</li>);
    } else {
      flushList();
      if (line.trim()) {
        pendingInline.push(renderInline(line, `ln-${idx}`));
      }
    }
  });
  flushInline();
  flushList();

  return output;
}

// ── extractNamePrice ──────────────────────────────────────────────────────────
// If a name starts with a price prefix (e.g. "$10 Chicken Tenders & Fries"),
// split it into { displayName, extractedPrice } so the price can be shown
// right-aligned. This handles the common scraper pattern of "$X Item Name".
// Returns null if no prefix price is found.

function extractNamePrice(name) {
  if (!name) return null;
  const match = name.match(/^\$(\d+(?:\.\d{1,2})?)\s+(.+)$/);
  if (!match) return null;
  return { displayName: match[2], extractedPrice: `$${match[1]}` };
}

// ── ContentItem ───────────────────────────────────────────────────────────────
// Renders one item row: bold name + optional price badge + optional description.
//
// Props:
//   name        {string}   Item name — shown as-is, or auto-split if price-prefixed
//   price       {string}   Raw price string — normalized to badge format
//   description {string}   Supporting detail — suppressed if redundant with name
//   noBorder    {boolean}  Remove bottom border (for last item in a list)

export function ContentItem({ name, price, description, noBorder }) {
  // Always strip a leading price prefix from the name (e.g. "$4 Alaskan Brewing cans" → "Alaskan Brewing cans")
  // Explicit price prop takes precedence for the badge; name prefix is stripped regardless.
  const nameSplit = extractNamePrice(name);
  const displayName = nameSplit ? nameSplit.displayName : (name || '');
  const rawPrice = price || (nameSplit ? nameSplit.extractedPrice : null);

  const badge = normalizePriceBadge(rawPrice);

  // Strip leading or trailing price token from description when a badge already shows it.
  // Normalize newlines to spaces — item descriptions are short inline text, not paragraphs.
  const displayDesc = description
    ? description
        .replace(/^\$[\d.,]+\s*/g, '')
        .replace(/[-–—]?\s*\$[\d.,]+\s*$/g, '')
        .replace(/\n+/g, ' ')
        .trim()
    : description;
  const showDesc = displayDesc && !isRedundantDescription(displayName, displayDesc);

  return (
    <div className={`ci-item-block${noBorder ? ' ci-no-border' : ''}`}>
      <div className="ci-item-row">
        <span className="ci-item-name">{displayName}</span>
        {badge && <span className="ci-item-price">{badge}</span>}
      </div>
      {showDesc && <div className="ci-item-desc">{renderMarkdown(displayDesc)}</div>}
    </div>
  );
}

// ── ContentItemSection ────────────────────────────────────────────────────────
// Renders a named section header above a list of items.
// Used when a Daily Special name sits above multiple parsed items.
//
// Props:
//   name    {string}   Section header label (e.g. "Wednesday Special")
//   items   {Array}    [{name, price, description?}] — rendered as ContentItem rows below header

export function ContentItemSection({ name, items = [] }) {
  return (
    <div className="ci-section">
      <div className="ci-section-header">
        <span className="ci-section-name">{name}</span>
      </div>
      <div className="ci-section-items">
        {items.map((item, i) => (
          <ContentItem
            key={i}
            name={item.name}
            description={item.description}
            price={item.price}
            noBorder={i === items.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
